'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import { Highlight } from '@eliadmin/core/src/components/ui/highlight';
import Link from '@eliadmin/core/src/components/link';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export type SearchBarBlogProps = {
  blog: Blog & { id: number };
  className?: {
    root?: string;
    imageContainer?: string;
    image?: string;
    info?: string;
    name?: string;
    description?: string;
  };
  searchQuery?: string;
};

export function SearchBarBlog({ blog, className, searchQuery }: SearchBarBlogProps) {
  function extractTextFromHtml(htmlContent: any) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  const imageUrl = blog.image?.url?.trim() || '/images/placeholder.png';

  return (
    <Link
      key={blog.id}
      href={blog.slug}
      className={cn(
        'grid cursor-pointer grid-cols-12 gap-2 p-4 hover:bg-gray-50',
        'ea-search-bar__product',
        className?.root,
      )}
    >
      {blog.image ? (
        <div
          className={cn(
            'h-13 w-13 col-span-2 flex flex-row items-center justify-center overflow-hidden rounded-sm bg-gray-200',
            'ea-search-bar__product-image-container',
            className?.imageContainer,
          )}
        >
          <Image
            alt={resolveImageAlt(blog?.image, blog?.name, 'image')}
            src={imageUrl}
            width="0"
            height="0"
            sizes={buildImageSizes([{ size: '100vw' }])}
            className={cn('h-auto w-full', 'ea-search-bar__product-image', className?.image)}
          />
        </div>
      ) : (
        <div
          className={cn(
            'h-13 w-13 col-span-2 flex flex-row items-center justify-center overflow-hidden rounded-sm bg-gray-200',
            'ea-search-bar__product-image-container',
            className?.imageContainer,
          )}
        >
          <Image
            alt={'image'}
            src={'/images/placeholder.png'}
            width="0"
            height="0"
            sizes={buildImageSizes([{ size: '100vw' }])}
            className={cn('h-auto w-full', 'ea-search-bar__product-image', className?.image)}
          />
        </div>
      )}
      <div
        className={cn('col-span-10 flex flex-col', 'ea-search-bar__product-info', className?.info)}
      >
        <h6 className={cn('mb-0 line-clamp-1', 'ea-search-bar__product-name', className?.name)}>
          <Highlight query={searchQuery ?? ''}>{blog.name}</Highlight>
        </h6>
        {/* TODO:
            highlight product description with some html rendering magic
        */}
        <HtmlRendererRaw
          className={cn(
            'line-clamp-2 text-ellipsis text-sm text-muted-foreground',
            'ea-search-bar__product-description',
            className?.description,
          )}
          html={extractTextFromHtml(blog.content)}
        />
      </div>
    </Link>
  );
}
